import { useState, useMemo, useEffect, useRef } from "react"

import { useLaundryContext } from "providers/LaundryProvider/LaundryProvider"
import useBookings from "hooks/useBookings"
import { useUI } from "providers/UIProvider"

import { Box, Flex, FormControl, Text, Image, Spinner } from "@chakra-ui/react"
import GEO from "assets/country_flags/georgia.png"

import Select from "react-select"
import { InputGroup, InputRightElement } from "@chakra-ui/react";
import Button from "components/UI/atoms/Button"
import FormLabel from "components/UI/atoms/FormLabel"
import Icon from "components/UI/atoms/Icon"
import CustomSelect from "components/UI/atoms/Select"
import Modal from "components/UI/organisms/Modal"
import { useReservationsContext } from "providers/ReservationsProvider/ReservationsProvider"
import { IClientVehicle, IReservationDetails, IWashType } from "types/reservation"
import { ICarBodyType } from "types/laundry"
import EditDetailsDiscount from "./EditDetailsDiscount"
import EditDetailsTotalPrice from "./EditDetailsTotalPrice"
import Input from "components/UI/atoms/Input"

const EditBookingDetailsModal = () => {
  const { modals, toggleModal, T } = useUI()
  const {
    form: {
      errors,
      touched,
    },
  } = useBookings()
  const isFirstRender = useRef(true);
  const { laundry } = useLaundryContext()

  const { currentReservation, handleRecalculateReservationAmount, updateReservationInfo, setState, updateReservationInfoLoading, discountTypesData, modifiedPrice, recalculateReservationAmountLoading } = useReservationsContext()
  const { clientVehicles, washTypes, clientVehicleId, washTypeId, vehicleBodyType, vehicleBodyTypeId, vehicleGovNumber, price, toTalPrice, discount, discountPercent } = currentReservation as IReservationDetails ?? {}
  const bodyTypes: ICarBodyType[] = laundry?.bodyTypes || []
  const getCurrentService: any = (serviceId: number) => washTypes.find((washType: IWashType) => washType.id === serviceId) || null
  const getCurrentVehicle: any = (vehicleId: number) =>
    clientVehicles.find((vehicle: IClientVehicle) => vehicle.id === vehicleId) ||
    { id: clientVehicleId, bodyType: vehicleBodyType, bodyTypeId: vehicleBodyTypeId, govNumber: vehicleGovNumber }

  const [selectedVehicle, setSelectedVehicle] = useState<IClientVehicle | null>(getCurrentVehicle(clientVehicleId))
  const [selectedService, setSelectedService] = useState<IWashType | null>(getCurrentService(washTypeId))
  const [selectedBodyType, setSelectedBodyType] = useState(vehicleBodyTypeId)
  // Useful in only Web created reservations
  const [currentPrice, setCurrentPrice] = useState(price)
  const [currentDiscountPercent, setCurrentDiscountPercent] = useState<any>(discountPercent == 0 ? "" : discountPercent)
  const [currentTotalPrice, setCurrentTotalPrice] = useState(toTalPrice)
  const discountTypeId = currentReservation!.discountTypeId ? currentReservation?.discountTypeId : 0
  const [selectedDiscountTypeId, setSelectedDiscountTypeId] = useState<number>(discountTypeId!)



  const generateServicesOpitons = () => washTypes.map((service: IWashType) => ({
    label: service.name,
    value: service.id
  }))
  const generateClientVehicles = () => clientVehicles.map((vehicle: IClientVehicle) => ({
    label: `${vehicle.govNumber} (${vehicle?.bodyType})`,
    value: vehicle.id
  }))

  const generateDiscountTypesData = () => {
    const customOption = {
      label: "No Discount",
      value: 0
    };

    const mappedData = discountTypesData?.map((discount: { name: string, id: number }) => ({
      label: discount.name,
      value: discount.id
    })) || [];

    return [customOption, ...mappedData];
  }


  const generateBodyTypesOptions = () => bodyTypes.map((bodyType) => ({
    label: bodyType.name,
    value: bodyType.id
  }))

  const dataChanged = useMemo(() => {
    if (clientVehicleId === selectedVehicle?.id && washTypeId === selectedService?.id && selectedBodyType === vehicleBodyTypeId) {
      return false
    }

    return true
  }, [selectedVehicle?.id, selectedService?.id, selectedBodyType])


  const handleBodyType = (e: { label: string, value: number }) => {
    setSelectedBodyType(e.value)
  }


  const handleService = (e: any) => {
    setSelectedService(getCurrentService(e.value))
  }



  useEffect(() => {
    if (currentReservation?.webReservation) {
      return
    }
    if (!isFirstRender.current) {
      handleRecalculateReservationAmount(selectedVehicle?.id ? selectedVehicle?.id : 0, selectedService!.id, selectedBodyType);
    }
    isFirstRender.current = false
    setState({ modifiedPrice: null })
  }, [selectedVehicle?.id, selectedService?.id, selectedBodyType])


  useEffect(() => {
    setSelectedBodyType(selectedVehicle!.bodyTypeId)
  }, [selectedVehicle])


  useEffect(() => {
    if (selectedDiscountTypeId === 0) {
      setCurrentDiscountPercent(null)
    }
  }, [selectedDiscountTypeId])

  console.log(modifiedPrice)


  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={modals.editReservationDetailsModal.isOpened}
      onClose={() => {
        toggleModal("editReservationDetailsModal", false)
        setState({ modifiedPrice: null })
      }}
      title={T("booking.details.edit.modal.title")}
      styles={{
        headerPadding: "20px 15px 20px 23px",
        width: "566px",
        bodyPadding: "0px 0px 0px 0px",
        height: "96%",
      }}
      closeButtonIcon="red-close"
      closeIconStyles={{ width: "33px", height: "33px", fill: "#7D90A8" }}
      closeOnOverlayClick={false}
    >
      <Box paddingBottom={"40px"}>
        <Flex
          gap={"30px"}
          padding="14px 23px 20px 23px"
          borderBottom={"1px solid #E9F2FD "}
        >
          <FormControl width={"55%"}>
            <FormLabel id="auto" text={T("booking.details.edit.auto")} style={{ fontWeight: "bold" }} />
            <CustomSelect
              styles={{
                singleValue: (base: any) => ({
                  ...base,
                  fontWeight: "bold"
                }),
                menuList: (base: any) => ({
                  ...base,
                  fontWeight: "bold"
                }),
              }}
              value={selectedBodyType || ""}
              onChange={handleBodyType}
              options={generateBodyTypesOptions()}
              inputStyles={{
                height: "60px",
              }}
              placeholder={selectedVehicle?.bodyType || ""}

            />
          </FormControl>
          <FormControl width={"45%"}>
            <FormLabel id="number" text={T("booking.details.edit.number")} style={{ fontWeight: "bold" }} />
            {/* Just Georgian Flag Div */}
            <Flex flexDirection="row">
              <Flex
                backgroundColor={"primary"}
                alignItems="center"
                width={"16%"}
                borderTopLeftRadius="8px"
                borderBottomLeftRadius="8px"
                flexDirection={"column"}
                justifyContent={"space-around"}
              >
                <Image
                  src={GEO}
                  alt="Flag"
                  w={"17px"}
                  h={"13px"}
                />
                <Text
                  lineHeight={"1px"}
                  fontSize={"10px"}
                  color={"white"}
                >
                  GE
                </Text>
              </Flex>
              <Select
                options={generateClientVehicles()}
                value={{ label: `${selectedVehicle?.govNumber} (${selectedVehicle?.bodyType})`, value: selectedVehicle?.id }}
                onChange={
                  (e) => {
                    setSelectedVehicle(getCurrentVehicle(e?.value))
                    if (e?.value === clientVehicleId) {
                      setState({ modifiedPrice: null })
                    }
                  }}
                components={clientVehicles?.length < 2 ? { DropdownIndicator: () => null, IndicatorSeparator: () => null } : { IndicatorSeparator: () => null }}
                openMenuOnClick={clientVehicles?.length > 1}
                isSearchable={false}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    height: "60px",
                    width: "177px",
                    borderRadius: "8px",
                    border: "2px solid #046CEC",
                    borderTopLeftRadius: "0px",
                    borderBottomLeftRadius: "0px",
                    boxShadow: "none",
                    ":hover": {
                      borderColor: "#046CEC"
                    },
                  }),
                }}
              />
            </Flex>
          </FormControl>
        </Flex>
        <Flex padding="14px 23px 20px 23px" borderBottom={"1px solid #E9F2FD "}>
          <FormControl width={"100%"}>
            <FormLabel id="auto" text={T("booking.details.edit.service")} style={{ fontWeight: "bold" }} />
            <CustomSelect
              value={selectedService?.id || ""}
              onChange={handleService}
              options={generateServicesOpitons()}
              inputStyles={{
                fontSize: "var(--chakra-fontSizes-m)",
                height: "60px",
              }}
              error={(touched.changeService && errors.changeService) && errors.changeService || undefined}
              styles={{
                singleValue: (base: any) => ({
                  ...base,
                  fontWeight: "bold"
                }),
                menuList: (base: any) => ({
                  ...base,
                  fontWeight: "bold"
                }),
              }}
            />
          </FormControl>
        </Flex>
        <Flex
          padding={"16px 23px"}
          justifyContent={"space-between"}
          borderBottom={"1px solid #E9F2FD"}
          width={"100%"}
          alignItems="center"
          height={"50px"}
        >
          <Text fontSize={"12px"}>
            {T("booking.details.edit.modal.service.init.price")}
          </Text>
          {currentReservation?.webReservation ?
            <Flex>
              <InputGroup>
                <Input
                  id="price"
                  name="price"
                  value={currentPrice}
                  onChange={(e: any) => setCurrentPrice(e.target.value)}
                  type="number"
                  style={{
                    fontSize: "18px",
                    color: "#7D90A8",
                    border: "1px solid #7D90A8",
                    borderRadius: "16px",
                    width: "75px",
                    height: "31px",
                    textAlign: "right",
                    paddingRight: "25px",
                  }}
                />
                <InputRightElement pointerEvents="none" width="30px" height="30px" fontSize="18px" zIndex="0">₾</InputRightElement>
              </InputGroup>

            </Flex>
            : <Flex
              alignItems={"center"}
              justifyContent="center"
              fontSize={"18px"}
              color={"#7D90A8"}
              height={"32px"}
              width="68px"
              border={"1px solid #7D90A8"}
              borderRadius={"16px"}
            >
              {price}₾
            </Flex>

          }
        </Flex>
        {currentReservation?.webReservation && <>
          <EditDetailsDiscount
            discount={discount}
            discountPercent={currentDiscountPercent}
            isDisabled={Boolean(!selectedDiscountTypeId)}
            handleDiscountPercent={(e) => setCurrentDiscountPercent(e.target.value)}
            discountType={currentReservation?.discountType}
            discountTypesData={generateDiscountTypesData()}
            handleDiscountTypeId={(e: any) => setSelectedDiscountTypeId(e.value)}
          />
          <EditDetailsTotalPrice
            totalPrice={currentTotalPrice} />
        </>}
        {!currentReservation?.webReservation &&
          <>
            <Flex
              padding={"16px 23px"}
              justifyContent={"space-between"}
              borderBottom={"1px solid #E9F2FD"}
              width={"100%"}
              alignItems="center"
              height={"50px"}
            >
              <Text fontSize={"12px"}>
                {T("booking.details.edit.modal.service.corrected.price")}
              </Text>
              <Flex
                alignItems={"center"}
                justifyContent="center"
                fontSize={"18px"}
                color={"success"}
                height={"32px"}
                width="68px"
                border={"1px solid #08BA36"}
                borderRadius={"16px"}
              >
                {recalculateReservationAmountLoading && <Spinner />}
                {(!recalculateReservationAmountLoading && modifiedPrice && dataChanged) ? `${modifiedPrice}₾` : "-"}
              </Flex>
            </Flex>
          </>
        }
        <Flex width="100%" justifyContent="center" mt={"40px"} gap={"30px"}>
          <Button
            text={T("actions.confirm")}
            onClick={() => {
              updateReservationInfo({
                reservationId: currentReservation!.id,
                personVehicleId: selectedVehicle?.id ? selectedVehicle?.id : 0,
                washTypeId: selectedService!.id,
                vehicleBodyTypeId: selectedBodyType,
                discountTypeId: selectedDiscountTypeId ? selectedDiscountTypeId : 0,
                discountPercent: Number(currentDiscountPercent),
                discount: (selectedDiscountTypeId || (currentDiscountPercent > 0 && currentDiscountPercent !== "")) ? true : false,
                price: currentPrice
              })
            }}
            variant="success"
            leftIcon={
              <Icon
                name="confirm"
                iconStyles={{ width: "30px", height: "30px" }}
              />
            }
            styles={{ width: "220px", height: "63px", fontSize: "m" }}
            _hover={{ backgroundColor: "#08BA36" }}
            isDisabled={!currentReservation?.webReservation && (!dataChanged || updateReservationInfoLoading)}
            isLoading={!currentReservation?.webReservation && updateReservationInfoLoading}
          />
          <Button
            text={T("actions.deny")}
            onClick={() => toggleModal("editReservationDetailsModal", false)}
            variant="error"
            leftIcon={
              <Icon
                name="close_rounded"
                iconStyles={{ width: "30px", height: "30px" }}
              />
            }
            styles={{ width: "220px", height: "63px", fontSize: "m" }}
          />
        </Flex>
      </Box>
    </Modal>
  )
}

export default EditBookingDetailsModal
